import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, User } from "lucide-react";

/**
 * Popup that prompts users without a profile photo to add one.
 * Shows once per session for caregivers and clients missing photos.
 */
const ProfilePhotoRequiredPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [userType, setUserType] = useState<"caregiver" | "client" | null>(null);

  useEffect(() => {
    if (!user) return;
    
    // Don't show on profile edit pages (user is probably already there to add a photo)
     const isOnProfilePage = location.pathname === "/mijn-praktijk/profiel" || 
                            location.pathname === "/mijn-profiel" ||
                            location.pathname === "/client-profiel-setup" ||
                            location.pathname === "/login" ||
                            location.pathname === "/kies-type";
    
    if (isOnProfilePage) return;

    checkProfilePhoto();
  }, [user, location.pathname]);

  const checkProfilePhoto = async () => {
    if (!user) return;

    try {
      // Throttle: show at most once per 3 days per user (localStorage persists across sessions)
      const THROTTLE_MS = 3 * 24 * 60 * 60 * 1000;
      const storageKey = `photo_popup_last_${user.id}`;
      const lastShownRaw = localStorage.getItem(storageKey);
      if (lastShownRaw) {
        const lastShown = parseInt(lastShownRaw, 10);
        if (!Number.isNaN(lastShown) && Date.now() - lastShown < THROTTLE_MS) {
          return;
        }
      }

      // Check admin setting - popup must be explicitly enabled
      const { data: setting } = await supabase
        .from("admin_settings")
        .select("setting_value")
        .eq("setting_key", "profile_photo_popup_enabled")
        .maybeSingle();

      if (setting?.setting_value !== "true") return;

      // First check if user is a caregiver
      const { data: caregiverProfile } = await supabase
        .from("caregiver_profiles")
        .select("id, profile_photo_url")
        .eq("user_id", user.id)
        .maybeSingle();

      if (caregiverProfile) {
        // User is a caregiver - check if they have a photo
        if (!caregiverProfile.profile_photo_url) {
          setUserType("caregiver");
          localStorage.setItem(storageKey, Date.now().toString());
          setOpen(true);
        }
        return;
      }

      // Check if user is a client
      const { data: clientProfile } = await supabase
        .from("client_profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (clientProfile) {
        // For clients, check job postings for client_photo_url
        const { data: jobPostings } = await supabase
          .from("job_postings")
          .select("client_photo_url")
          .eq("user_id", user.id)
          .not("client_photo_url", "is", null)
          .limit(1);

        if (!jobPostings || jobPostings.length === 0) {
          setUserType("client");
          localStorage.setItem(storageKey, Date.now().toString());
          setOpen(true);
        }
      }
    } catch (error) {
      console.error("Error checking profile photo:", error);
    }
  };

  const handleAddPhoto = () => {
    setOpen(false);
    if (userType === "caregiver") {
      navigate("/mijn-praktijk/profiel");
    } else {
      // For clients, navigate to their most recent job posting edit page
      navigateToClientPhotoUpload();
    }
  };

  const navigateToClientPhotoUpload = async () => {
    try {
      const { data: jobPosting } = await supabase
        .from("job_postings")
        .select("id")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (jobPosting) {
        navigate(`/bewerk-hulpvraag/${jobPosting.id}`);
      } else {
        navigate("/client-dashboard");
      }
    } catch {
      navigate("/client-dashboard");
    }
  };

  const handleLater = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Camera className="w-7 h-7 text-primary" />
          </div>
          <DialogTitle className="text-xl font-serif">
            Voeg een profielfoto toe
          </DialogTitle>
          <DialogDescription className="pt-2 text-base">
            {userType === "caregiver" 
              ? "Een profielfoto maakt je profiel persoonlijker en vergroot je kans op matches met cliënten."
              : "Een foto helpt zorgverleners om je beter te leren kennen."}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4 my-2">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/15 rounded-full">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">
                Profielen met foto krijgen tot <strong className="text-foreground">3x meer reacties</strong>
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col pt-2">
          <Button onClick={handleAddPhoto} className="w-full" size="lg">
            <Camera className="w-4 h-4 mr-2" />
            Foto toevoegen
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleLater} 
            className="w-full text-muted-foreground text-sm"
          >
            Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePhotoRequiredPopup;
